// Single source of truth for the portal sidebar.
// Sidebar component renders this recursively and lights up the active item
// based on the current Inertia URL.

import type { CompanyLifecycleState } from "@/types/inertia"

export type NavVisibilityCtx = {
  lifecycleState?: CompanyLifecycleState
}

export type NavItem = {
  label: string
  icon?: string
  href?: string
  /** Optional badge count rendered as a chip on the right. Live for
   *  Tickets queue children (sourced from the `ticketsNav` shared prop). */
  count?: number
  /** Capability key from config/capabilities.yml. When present, the
   *  Sidebar hides this item from any user who lacks the capability. A
   *  parent item with children is hidden only when ALL of its children
   *  are also hidden — so e.g. agents see "Settings → My Profile / My
   *  Security" without the rest of the Settings group. */
  requiresCapability?: string
  /** Optional dynamic visibility predicate. When present and returns
   *  false, the item is dropped from the rendered tree. Used by the
   *  Onboarding tab to disappear once the company finishes onboarding —
   *  the page itself stays reachable at the URL. */
  visible?: (ctx: NavVisibilityCtx) => boolean
  children?: NavItem[]
}

export const navigation: NavItem[] = [
  { label: "Dashboard", icon: "dashboard", href: "/dashboard", requiresCapability: "dashboard_view" },
  // Tickets — children are rendered DYNAMICALLY by the Sidebar component
  // from the `ticketsNav` shared inertia prop (All / My / per-queue rows
  // with live counts). Don't add static children here; they get hidden
  // by the dynamic merge.
  { label: "Tickets", icon: "tickets", href: "/tickets" },
  {
    label: "AI Agents", icon: "ai-agents", href: "/agents",
    requiresCapability: "agents_view",
    children: [
      { label: "All AI Agents", href: "/agents" },
      { label: "Skills", href: "/skills", requiresCapability: "skills_view" },
      { label: "Testing Dashboard", href: "/testing", requiresCapability: "skills_edit" },
      { label: "Integration Bench", href: "/testing/integration", requiresCapability: "skills_edit" },
    ],
  },
  {
    label: "Knowledge", icon: "knowledge", href: "/topics",
    requiresCapability: "agents_view",
    children: [
      { label: "Topics", href: "/topics" },
      { label: "FAQ Entries", href: "/faq_entries" },
    ],
  },
  {
    label: "Escalation", icon: "escalation", href: "/escalation_policies",
    children: [
      { label: "Policies", href: "/escalation_policies", requiresCapability: "escalation_policies_edit" },
      { label: "Reasons", href: "/escalation_reasons", requiresCapability: "agents_view" },
      { label: "Support Pools", href: "/agent_pools", requiresCapability: "agent_pools_edit" },
    ],
  },
  {
    label: "Integrations", icon: "integrations", href: "/endpoints",
    children: [
      { label: "Endpoints", href: "/endpoints", requiresCapability: "endpoints_view" },
      { label: "Custom Fields", href: "/custom_fields", requiresCapability: "custom_fields_view" },
      { label: "Apps", href: "/settings/apps" },
      { label: "API Keys & SDK", href: "/settings?tab=integration", requiresCapability: "api_keys_view" },
      { label: "Onboarding", href: "/settings/onboarding", requiresCapability: "support_connections_view",
        visible: (ctx) => ctx.lifecycleState === "onboarding" || ctx.lifecycleState === undefined },
    ],
  },
  {
    label: "Feedback", icon: "feedback", href: "/user_feedback",
    requiresCapability: "user_feedback_view",
  },
  {
    label: "Analytics", icon: "analytics", href: "/analytics",
    requiresCapability: "analytics_view",
    children: [
      { label: "Overview",     href: "/analytics" },
      { label: "Performance",  href: "/analytics/handlers" },
      { label: "Escalations",  href: "/analytics/escalations" },
      { label: "SLA Performance", href: "/analytics/sla" },
    ],
  },
  {
    label: "Settings", icon: "settings", href: "/settings?tab=profile",
    children: [
      { label: "Company",     href: "/settings?tab=company", requiresCapability: "settings_company_profile" },
      { label: "Team",        href: "/settings/team",        requiresCapability: "team_view" },
      { label: "My Profile",  href: "/settings?tab=profile" },
      { label: "My Security", href: "/settings?tab=security" },
    ],
  },
]
